import { useQuery } from "@tanstack/react-query";
import { adminApi } from "../api/adminApi";
import { Card, CardContent, CardHeader, Badge } from "@learn-me/ui";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ClipboardCheck,
} from "lucide-react";

interface PublishValidationProps {
  courseId: string;
  onValidationChange?: (canPublish: boolean) => void;
}

export function PublishValidation({
  courseId,
  onValidationChange,
}: PublishValidationProps) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["course-validation", courseId],
    queryFn: () => adminApi.validateCourseForPublishing(courseId),
    staleTime: 30000, // Cache for 30 seconds
  });

  // Notify parent of validation status changes
  if (data && onValidationChange) {
    onValidationChange(data.canPublish);
  }

  const errors = data?.issues.filter((i) => i.severity === "error") || [];
  const warnings = data?.issues.filter((i) => i.severity === "warning") || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Validacion
          </h2>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Validacion
        </h2>
        <div className="flex items-center gap-2">
          {data?.canPublish ? (
            <Badge variant="success" className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Listo
            </Badge>
          ) : (
            <Badge variant="destructive" className="flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              Pendiente
            </Badge>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-1 hover:bg-muted rounded transition-colors"
            title="Actualizar validacion"
          >
            <RefreshCw
              className={`h-4 w-4 text-muted-foreground ${isFetching ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.canPublish && data.issues.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span>El curso cumple todos los requisitos para publicarse.</span>
          </div>
        ) : (
          <>
            {/* Errores (bloquean publicacion) */}
            {errors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                  <XCircle className="h-4 w-4" />
                  Errores ({errors.length})
                </p>
                <ul className="space-y-1">
                  {errors.map((issue) => (
                    <li
                      key={issue.code}
                      className="text-sm text-red-600 bg-red-50 p-2 rounded flex items-start gap-2"
                    >
                      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Advertencias (recomendaciones) */}
            {warnings.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Recomendaciones ({warnings.length})
                </p>
                <ul className="space-y-1">
                  {warnings.map((issue) => (
                    <li
                      key={issue.code}
                      className="text-sm text-amber-600 bg-amber-50 p-2 rounded flex items-start gap-2"
                    >
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {!data?.canPublish && (
          <p className="text-xs text-muted-foreground">
            Corrige los errores para poder publicar el curso.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
